
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { User, Lock, ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMagicLink } from "@/hooks/useMagicLink";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Login() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const { sendMagicLink, loading: magicLinkLoading } = useMagicLink();

  useEffect(() => {
    console.log("Current URL:", window.location.href);
    const errorParam = searchParams.get('error');
    const errorDescriptionParam = searchParams.get('error_description');
    if (errorParam || errorDescriptionParam) {
      console.error("Auth Error:", errorParam, errorDescriptionParam);
      setError(errorDescriptionParam || errorParam || 'En autentiseringsfeil oppstod');
    }

    if (user) {
      navigate("/dashboard");
      return;
    }

    const handleAuthRedirect = async () => {
      const access_token = searchParams.get('access_token');
      const refresh_token = searchParams.get('refresh_token');
      const type = searchParams.get('type');
      
      if (access_token && refresh_token) {
        try {
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token
          });

          if (error) {
            console.error('Session set error:', error);
            setError('Ugyldig eller utløpt autentiseringsøkt');
            return;
          }

          navigate('/dashboard');
          toast({
            title: "Innlogget",
            description: "Du er nå logget inn",
          });
        } catch (err) {
          console.error('Token verification error:', err);
          setError('Det oppstod en feil ved verifisering av påloggingen');
        }
      }
      
      if (type === 'recovery' && access_token) {
        try {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: access_token,
            type: 'recovery'
          });

          if (error) {
            console.error('Password reset error:', error);
            setError('Ugyldig eller utløpt lenke for tilbakestilling av passord');
            return;
          }

          navigate('/change-password');
          toast({
            title: "Tilbakestill passord",
            description: "Vennligst skriv inn ditt nye passord",
          });
        } catch (err) {
          console.error('Password reset verification error:', err);
          setError('Det oppstod en feil ved verifisering av tilbakestillingslenken');
        }
      }
    };

    handleAuthRedirect();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event, session?.user?.id);
      if (event === 'SIGNED_IN') {
        console.log("Sign in successful, redirecting...");
        navigate("/dashboard");
      } else if (event === 'SIGNED_OUT') {
        setError(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, toast, searchParams, user]);

  const handleMagicLinkLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({
        variant: "destructive",
        title: "Ugyldig e-post",
        description: "Vennligst oppgi en gyldig e-postadresse",
      });
      return;
    }
    await sendMagicLink(email);
  };

  const handleDisableEmailConfirmation = async () => {
    try {
      if (!user) {
        const { data: { user: signedUpUser }, error } = await supabase.auth.signUp({
          email: email,
          password: 'tempPassword123', // This is just a placeholder
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
            data: {
              email_confirmed: true // Marking user as confirmed
            }
          }
        });

        if (error) {
          console.error('Signup error:', error);
          setError(error.message);
          return;
        }

        if (signedUpUser) {
          toast({
            title: "Bruker opprettet",
            description: "Bruk magisk lenke for å logge inn",
          });
          
          // Send magic link after creating user
          await sendMagicLink(email);
        }
      }
    } catch (error) {
      console.error('Error in handleDisableEmailConfirmation:', error);
      setError('Det oppstod en feil ved opprettelse av konto');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 to-white">
      <header className="w-full p-4 flex items-center justify-between bg-white/80 backdrop-blur-sm border-b border-orange-100">
        <Button
          variant="ghost"
          className="text-orange-800 hover:text-orange-900"
          onClick={() => navigate("/")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Tilbake til forsiden
        </Button>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/80 backdrop-blur-sm border-orange-100">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2 text-gray-900">
              <User className="h-6 w-6 text-orange-800" />
              Velkommen til AdvoKAI
            </CardTitle>
            <CardDescription className="text-center text-gray-700">
              Logg inn på din konto eller opprett en ny
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <Tabs defaultValue="standard" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="standard">Standard pålogging</TabsTrigger>
                <TabsTrigger value="magic">Magisk lenke</TabsTrigger>
              </TabsList>
              
              <TabsContent value="standard">
                <Auth
                  supabaseClient={supabase}
                  appearance={{
                    theme: ThemeSupa,
                    variables: {
                      default: {
                        colors: {
                          brand: 'rgb(154 52 18)',
                          brandAccent: 'rgb(124 45 18)',
                          inputText: 'rgb(17 24 39)',
                          inputBackground: 'rgb(255 255 255)',
                          inputBorder: 'rgb(209 213 219)',
                          inputBorderFocus: 'rgb(154 52 18)',
                          inputBorderHover: 'rgb(124 45 18)',
                          inputPlaceholder: 'rgb(107 114 128)',
                        },
                      },
                    },
                    className: {
                      container: 'w-full',
                      button: 'w-full bg-orange-800 hover:bg-orange-900 text-white font-medium',
                      input: 'border-gray-300 focus:border-orange-800',
                      label: 'text-gray-900 font-medium',
                      anchor: 'text-orange-800 hover:text-orange-900 font-medium',
                    },
                  }}
                  providers={[]}
                  redirectTo={`${window.location.origin}/auth/callback`}
                  localization={{
                    variables: {
                      sign_in: {
                        email_label: 'E-postadresse',
                        password_label: 'Passord',
                        email_input_placeholder: 'Din e-postadresse',
                        password_input_placeholder: 'Ditt passord',
                        button_label: 'Logg inn',
                        loading_button_label: 'Logger inn...',
                        social_provider_text: 'Logg inn med {{provider}}',
                        link_text: 'Har du allerede en konto? Logg inn',
                      },
                      sign_up: {
                        email_label: 'E-postadresse',
                        password_label: 'Passord',
                        email_input_placeholder: 'Din e-postadresse',
                        password_input_placeholder: 'Velg et sterkt passord',
                        button_label: 'Registrer deg',
                        loading_button_label: 'Registrerer...',
                        social_provider_text: 'Registrer deg med {{provider}}',
                        link_text: 'Har du ikke en konto? Registrer deg',
                      },
                      forgotten_password: {
                        email_label: 'E-postadresse',
                        password_label: 'Passord',
                        email_input_placeholder: 'Din e-postadresse',
                        button_label: 'Send tilbakestillingslenke',
                        loading_button_label: 'Sender...',
                        link_text: 'Glemt passord?',
                      },
                    },
                  }}
                />
                
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600 mb-2">Problemer med e-postbekreftelse?</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate("/auth/magic-link")}
                    className="w-full text-orange-800 border-orange-800 hover:bg-orange-50"
                  >
                    Bruk magisk lenke i stedet
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="magic">
                <form onSubmit={handleMagicLinkLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-postadresse</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="din@epost.no"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-orange-800 hover:bg-orange-900 text-white"
                    disabled={magicLinkLoading}
                  >
                    {magicLinkLoading ? 'Sender...' : 'Send magisk innloggingslenke'}
                    <Mail className="ml-2 h-4 w-4" />
                  </Button>
                  
                  <div className="text-center text-sm text-gray-600 mt-4">
                    <p>Du vil motta en e-post med en lenke du kan bruke for å logge inn</p>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <div className="text-xs text-center text-gray-600 w-full">
              <p>Har du problemer med å bekrefte e-posten din?</p>
              <Button 
                variant="link" 
                className="text-orange-800 p-0 h-auto text-xs"
                onClick={handleDisableEmailConfirmation}
              >
                Klikk her for å registrere deg uten e-postverifisering
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>

      <footer className="w-full p-4 bg-white/80 backdrop-blur-sm border-t border-orange-100 text-center text-gray-700">
        <p>© 2024 AdvoKAI. Alle rettigheter forbeholdt.</p>
      </footer>
    </div>
  );
}
